import { isElectron } from "../utils/platform";

export interface DeploymentConfig {
  deploymentMode: "single" | "multi";
  tenantResolutionMode: "fixed" | "path" | "domain";
  storefrontRouteMode: "root" | "slug";
  webRouterType: "browser" | "hash";
}

let deploymentConfig: DeploymentConfig | null = null;

export function parseDeploymentConfig(payload: unknown): DeploymentConfig {
  const candidate =
    payload &&
    typeof payload === "object" &&
    "data" in payload &&
    (payload as { data?: unknown }).data
      ? (payload as { data: unknown }).data
      : payload;
  if (!candidate || typeof candidate !== "object") {
    throw new Error("Deployment configuration is not an object");
  }

  const config = candidate as Partial<DeploymentConfig>;
  const valid =
    (config.deploymentMode === "single" ||
      config.deploymentMode === "multi") &&
    (config.tenantResolutionMode === "fixed" ||
      config.tenantResolutionMode === "path" ||
      config.tenantResolutionMode === "domain") &&
    (config.storefrontRouteMode === "root" ||
      config.storefrontRouteMode === "slug") &&
    (config.webRouterType === "browser" || config.webRouterType === "hash");
  if (!valid) throw new Error("Deployment configuration has invalid values");

  const isSingleFixed =
    config.deploymentMode === "single" &&
    config.tenantResolutionMode === "fixed" &&
    config.storefrontRouteMode === "root" &&
    config.webRouterType === "browser";
  const isMultiDomain =
    config.deploymentMode === "multi" &&
    config.tenantResolutionMode === "domain" &&
    config.storefrontRouteMode === "root" &&
    config.webRouterType === "browser";
  const isMultiPath =
    config.deploymentMode === "multi" &&
    config.tenantResolutionMode === "path" &&
    config.storefrontRouteMode === "slug" &&
    config.webRouterType === "hash";
  if (!isSingleFixed && !isMultiDomain && !isMultiPath) {
    throw new Error("Deployment configuration contains an unsupported mode");
  }
  return config as DeploymentConfig;
}

export function setDeploymentConfig(config: DeploymentConfig): void {
  deploymentConfig = config;
}

export function getDeploymentConfig(): DeploymentConfig {
  if (!deploymentConfig) {
    throw new Error("Deployment configuration has not been initialized");
  }
  return deploymentConfig;
}

export function isRootStorefront(): boolean {
  return getDeploymentConfig().storefrontRouteMode === "root";
}

export function usesHashRouter(): boolean {
  return isElectron() || getDeploymentConfig().webRouterType === "hash";
}

export function isLegacySlugHash(hash: string): boolean {
  const normalized = hash.replace(/^#/, "").split("?")[0].replace(/\/+$/, "");
  return normalized === "/site" || normalized.startsWith("/site/");
}

export function currentRouterPath(): string {
  if (typeof window === "undefined") return "/";
  return usesHashRouter()
    ? window.location.hash.slice(1).split("?")[0] || "/"
    : window.location.pathname;
}

export function dashboardPath(path = "/"): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  if (!isRootStorefront()) return normalized;
  if (normalized === "/dashboard" || normalized.startsWith("/dashboard/")) {
    return normalized;
  }
  return normalized === "/" ? "/dashboard" : `/dashboard${normalized}`;
}

export function storefrontRoot(slug: string): string {
  return isRootStorefront() ? "/" : `/site/${slug}`;
}

export function storefrontPath(slug: string, path = ""): string {
  const normalizedPath = path ? `/${path.replace(/^\/+/, "")}` : "";
  const root = storefrontRoot(slug);
  return root === "/" ? normalizedPath || "/" : `${root}${normalizedPath}`;
}

export function storefrontOrderPath(slug: string, orderId: string): string {
  return isRootStorefront()
    ? `/profile/orders/${orderId}`
    : storefrontPath(slug, `/orders/${orderId}`);
}

export function isStorefrontPath(pathname: string): boolean {
  const normalized = pathname.split("?")[0].replace(/\/+$/, "") || "/";
  if (!isRootStorefront()) {
    return normalized === "/site" || normalized.startsWith("/site/");
  }
  return (
    normalized === "/" ||
    normalized === "/products" ||
    normalized.startsWith("/products/") ||
    normalized === "/about" ||
    normalized === "/contact" ||
    normalized === "/profile" ||
    normalized.startsWith("/profile/") ||
    normalized === "/cart" ||
    normalized === "/checkout" ||
    normalized === "/categories" ||
    normalized.startsWith("/categories/")
  );
}
